import { useState, useEffect, useCallback } from 'react';

import {
  pushNotificationManager,
  type PushSubscriptionData,
} from '../lib/push-notifications';
import { useAuth } from '../app/providers/AuthProvider';
import { useFirebaseServices } from '../app/providers/FirebaseProvider';
import {
  saveUserSubscription,
  removeUserSubscription,
} from '../features/notifications/api/subscriptionRepository';

export interface UsePushNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission;
  subscription: PushSubscriptionData | null;
  isSubscribed: boolean;
  loading: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  requestPermission: () => Promise<void>;
  showTestNotification: () => void;
}

export const usePushNotifications = (): UsePushNotificationsReturn => {
  const { user } = useAuth();
  const { firestore } = useFirebaseServices();

  const [isSupported] = useState(pushNotificationManager.isSupported());
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscription, setSubscription] = useState<PushSubscriptionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSubscribed = subscription !== null;

  // Initialize push notifications
  const initialize = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const initialized = await pushNotificationManager.initialize();
      if (initialized) {
        setPermission(pushNotificationManager.getPermissionStatus());
        const currentSubscription = await pushNotificationManager.getSubscription();
        setSubscription(currentSubscription);
      } else {
        setError('Failed to initialize push notifications');
      }
    } catch (initError) {
      const errorMessage =
        initError instanceof Error
          ? initError.message
          : 'Failed to initialize push notifications';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const newPermission = await pushNotificationManager.requestPermission();
      setPermission(newPermission);

      if (newPermission !== 'granted') {
        throw new Error('Notification permission was not granted');
      }
    } catch (permError) {
      const errorMessage =
        permError instanceof Error
          ? permError.message
          : 'Failed to request notification permission';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!user) {
      setError('You must be signed in to subscribe to notifications');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newSubscription = await pushNotificationManager.subscribe();
      setSubscription(newSubscription);
      setPermission(pushNotificationManager.getPermissionStatus());

      // Save subscription to Firestore
      if (newSubscription) {
        await saveUserSubscription(firestore, user.uid, newSubscription);
      }
    } catch (subError) {
      const errorMessage =
        subError instanceof Error
          ? subError.message
          : 'Failed to subscribe to notifications';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user, firestore]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const success = await pushNotificationManager.unsubscribe();
      if (success) {
        setSubscription(null);

        // Remove subscription from Firestore
        if (user) {
          await removeUserSubscription(firestore, user.uid);
        }
      } else {
        throw new Error('Failed to unsubscribe');
      }
    } catch (unsubError) {
      const errorMessage =
        unsubError instanceof Error
          ? unsubError.message
          : 'Failed to unsubscribe from notifications';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user, firestore]);

  // Show test notification
  const showTestNotification = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      await pushNotificationManager.showTestNotification();
    } catch (testError) {
      const errorMessage =
        testError instanceof Error
          ? testError.message
          : 'Failed to show test notification';
      setError(errorMessage);
      console.error('Test notification failed:', testError);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize on mount and when user changes
  useEffect(() => {
    if (isSupported) {
      void initialize();
    }
  }, [initialize, isSupported]);

  // Update permission status on focus
  useEffect(() => {
    if (!isSupported) return;

    const handleFocus = () => {
      const currentPermission = pushNotificationManager.getPermissionStatus();
      setPermission(currentPermission);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isSupported]);

  return {
    isSupported,
    permission,
    subscription,
    isSubscribed,
    loading,
    error,
    initialize,
    subscribe,
    unsubscribe,
    requestPermission,
    showTestNotification,
  };
};
